import './SkillJudge.css';

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

export interface SkillJudgeProps {
  title: string;
  value?: number;
  onChange?: (value: number) => void;
  maxRating?: number;
}

const PAD = 4;
/** Must match `.rating-segment-row` gap — wider gaps expose more of the sliding pill between cells. */
const GAP = 6;
const DEFAULT_MAX_RATING = 5;

const clampMaxRating = (n: number): number =>
  Number.isFinite(n) ? Math.min(DEFAULT_MAX_RATING, Math.max(1, Math.floor(n))) : DEFAULT_MAX_RATING;

const pillTransform = (xPx: number): string =>
  `translate3d(${xPx}px, 0, 0)`;

const SkillJudge: React.FC<SkillJudgeProps> = ({
  title,
  value = 0,
  onChange,
  maxRating = DEFAULT_MAX_RATING,
}) => {
  const cappedMaxRating = clampMaxRating(maxRating);
  const segmentCount = cappedMaxRating + 1;
  const safeValue = Math.max(0, Math.min(cappedMaxRating, Math.floor(value)));
  const readOnly = onChange == null;
  const barRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const hasPaintedRef = useRef(false);
  const prevValueRef = useRef<number>(safeValue);
  const valueRef = useRef(safeValue);
  valueRef.current = safeValue;

  const applyPillInstant = useCallback((v: number): void => {
    const bar = barRef.current;
    const pill = pillRef.current;
    if (!bar || !pill) return;

    const cs = window.getComputedStyle(bar);
    const padL = Number.parseFloat(cs.paddingLeft) || PAD;
    const padR = Number.parseFloat(cs.paddingRight) || PAD;
    const inner = bar.clientWidth - padL - padR;
    const cellW = (inner - GAP * (segmentCount - 1)) / segmentCount;
    const xAt = (idx: number): number => padL + idx * (cellW + GAP);

    pill.style.transition = 'none';
    pill.style.width = `${cellW}px`;
    if (v >= 1) {
      pill.style.opacity = '1';
      pill.style.transform = pillTransform(xAt(v));
    } else {
      pill.style.opacity = '0';
      pill.style.transform = pillTransform(xAt(0));
    }
    requestAnimationFrame(() => {
      pill.style.transition = '';
    });
  }, [segmentCount]);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const pill = pillRef.current;
    if (!bar || !pill) return;

    const cs = window.getComputedStyle(bar);
    const padL = Number.parseFloat(cs.paddingLeft) || PAD;
    const padR = Number.parseFloat(cs.paddingRight) || PAD;
    const inner = bar.clientWidth - padL - padR;
    const cellW = (inner - GAP * (segmentCount - 1)) / segmentCount;
    const xAt = (idx: number): number => padL + idx * (cellW + GAP);

    pill.style.width = `${cellW}px`;

    if (!hasPaintedRef.current) {
      hasPaintedRef.current = true;
      applyPillInstant(safeValue);
      prevValueRef.current = safeValue;
      return;
    }

    const prev = prevValueRef.current;
    if (prev === safeValue) {
      applyPillInstant(safeValue);
      return;
    }

    let startX = xAt(safeValue);
    let endX = xAt(safeValue);
    let startOp = '1';
    let endOp = '1';

    if (safeValue === 0) {
      /* N/A: fade out on the previous column — no slide toward the 0 lane */
      startX = xAt(prev);
      endX = xAt(prev);
      startOp = '1';
      endOp = '0';
    } else if (prev === 0) {
      /* First score: appear in place — no slide in from off-screen / zero */
      startX = xAt(safeValue);
      endX = xAt(safeValue);
      startOp = '0';
      endOp = '1';
    } else {
      startX = xAt(prev);
      endX = xAt(safeValue);
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pillMoves = startX !== endX;
    const activeTransition = pillMoves
      ? 'transform 0.42s cubic-bezier(0.2, 0.88, 0.22, 1), opacity 0.34s cubic-bezier(0.25, 0.1, 0.25, 1)'
      : 'opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';

    pill.style.transition = 'none';
    pill.style.opacity = startOp;
    pill.style.transform = pillTransform(startX);

    prevValueRef.current = safeValue;

    if (reducedMotion) {
      pill.style.transition = 'none';
      pill.style.opacity = endOp;
      pill.style.transform = pillTransform(endX);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        pill.style.transition = activeTransition;
        pill.style.opacity = endOp;
        pill.style.transform = pillTransform(endX);
      });
    });
  }, [safeValue, segmentCount, applyPillInstant]);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    const ro = new ResizeObserver(() => {
      applyPillInstant(valueRef.current);
    });
    ro.observe(bar);
    return () => ro.disconnect();
  }, [applyPillInstant]);

  const handleRatingChange = (rating: number): void => {
    onChange?.(rating);
  };

  return (
    <div
      className={['skill-judge', readOnly ? 'skill-judge-readonly' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className="skill-header">
        <h3>{title}</h3>
        {/* <div
          className="tooltip-trigger"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span className="question-mark">?</span>
          {showTooltip ? (
            <div className="tooltip" role="tooltip">
              <div className="tooltip-content">
                <p className="tooltip-title">Judging Scale</p>
                <div className="tooltip-scale">
                  <div className="scale-item">
                    <span className="scale-number">1</span>
                    <span className="scale-label">-  Poor</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-number">2</span>
                    <span className="scale-label">-  Needs Work</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-number">3</span>
                    <span className="scale-label">-  Average</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-number">4</span>
                    <span className="scale-label">-  Above Average</span>
                  </div>
                  <div className="scale-item">
                    <span className="scale-number">5</span>
                    <span className="scale-label">-  Excellent</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div> */}
      </div>

      <div className="rating-bar" ref={barRef}>
        <div className="rating-segment-pill" ref={pillRef} aria-hidden="true" />
        <div className="rating-segment-row">
          {Array.from({ length: segmentCount }, (_, rating) => rating).map((rating) => (
            <div
              key={rating}
              className={[
                'rating-segment',
                safeValue === rating ? 'selected' : '',
                rating === 0 ? 'zero' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={
                readOnly
                  ? undefined
                  : () => {
                      handleRatingChange(rating);
                    }
              }
            >
              {rating}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkillJudge;
